/**
 * Composable para gestión unificada de logs de auditoría
 * Proporciona funciones reutilizables para trackear cambios en documentos
 */

import store from '@/store'
import { oid } from '@/helpers/mongoHelpers'
import { detectChangedFieldsWithValues, createAuditEntry } from '@/helpers/auditLog'
import { useLambda } from '@/views/habit/awsLambda'

export default function useAuditLog() {
  const { updateItem } = useLambda()
  const userData = store.state?.userStore?.userData
  const userId = oid(userData?.worker_id?.$oid || userData?._id)
  const userName = userData?.username || userData?.email || 'Usuario'

  /**
   * Añade entrada de auditoría después de una actualización
   * Detecta automáticamente campos modificados y los trackea con valores from/to
   *
   * @param {Object} params - Parámetros
   * @param {String} params.collection - Colección MongoDB (ej: 'role', 'gap', 'case')
   * @param {String} params.documentId - ID del documento a auditar
   * @param {Object} params.originalData - Datos originales antes del cambio
   * @param {Object} params.updatedData - Datos actualizados
   * @param {String} [params.action='actualizar'] - Acción realizada
   * @param {Array} [params.excludeFields=[]] - Campos adicionales a excluir del tracking
   * @param {Boolean} [params.forceLog=false] - Forzar log aunque no haya cambios
   */
  const addAuditEntry = async ({
    collection,
    documentId,
    originalData,
    updatedData,
    action = 'actualizar',
    excludeFields = [],
    forceLog = false,
  }) => {
    const changedFields = detectChangedFieldsWithValues(
      originalData,
      updatedData,
      excludeFields,
    )

    // Si no hay cambios y no es un log forzado, no registrar
    if (Object.keys(changedFields).length === 0 && !forceLog) return

    const auditEntry = createAuditEntry(userId, userName, action, changedFields)

    await updateItem({
      collection,
      query: { _id: oid(documentId), client_id: oid(userData.client.$oid) },
      action: { $push: { history: auditEntry } },
    })
  }

  /**
   * Registra acción sin cambios de campos (acciones administrativas)
   * Usado para: envío de emails, validaciones, recordatorios, etc.
   * Estas acciones no modifican el documento pero deben quedar registradas
   *
   * @param {Object} params - Parámetros
   * @param {String} params.collection - Colección MongoDB
   * @param {String} params.documentId - ID del documento
   * @param {String} params.action - Acción (ej: 'envio_recordatorio', 'validar')
   * @param {String} [params.detail] - Detalle adicional opcional
   */
  const logAction = async ({
    collection,
    documentId,
    action,
    detail = null,
  }) => {
    const auditEntry = createAuditEntry(userId, userName, action, {})
    if (detail) auditEntry.detail = detail

    await updateItem({
      collection,
      query: { _id: oid(documentId), client_id: oid(userData.client.$oid) },
      action: { $push: { history: auditEntry } },
    })
  }

  /**
   * Crea entrada inicial para nuevos documentos (action='crear')
   * Usar al crear documentos para tener log desde el inicio
   *
   * @returns {Object} Entrada de auditoría inicial con changedFields={}
   */
  const createInitialAuditEntry = () => createAuditEntry(userId, userName, 'crear', {})

  return {
    addAuditEntry,
    logAction,
    createInitialAuditEntry,
    userId,
    userName,
  }
}
